import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Cpu } from "lucide-react";
import api from "../lib/api";

let _cache = null;

export function ModelPicker({ value, onChange, size = "md", align = "left" }) {
  const [models, setModels] = useState(_cache?.models || []);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (_cache) { setModels(_cache.models); return; }
    api.get("/projects/models").then((r) => { _cache = r.data; setModels(r.data.models); }).catch(() => {});
  }, []);

  const place = () => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const menuH = Math.min(360, 60 + models.length * 46);
    const spaceBelow = window.innerHeight - b.bottom;
    const up = spaceBelow < menuH + 16 && b.top > spaceBelow;
    const style = { position: "fixed", width: 240, zIndex: 9999 };
    if (up) style.bottom = window.innerHeight - b.top + 8;
    else style.top = b.bottom + 8;
    if (align === "right") style.right = window.innerWidth - b.right;
    else style.left = b.left;
    setPos(style);
  };

  useLayoutEffect(() => { if (open) place(); /* eslint-disable-next-line */ }, [open, models.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollResize = () => setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onScrollResize);
    window.addEventListener("scroll", onScrollResize, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onScrollResize);
      window.removeEventListener("scroll", onScrollResize, true);
    };
  }, [open]);

  const current = models.find((m) => m.id === value) || models.find((m) => m.id === (_cache?.default)) || models[0];
  const groups = [...new Set(models.map((m) => m.group))];
  const pad = size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm";

  return (
    <>
      <button ref={btnRef} data-testid="model-picker-button" onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 ${pad} rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] transition-colors font-medium`}>
        <Cpu className="h-3.5 w-3.5 text-indigo-400" />
        <span className="max-w-[130px] truncate">{current?.label || "Model"}</span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} data-testid="model-picker-menu" style={pos} className="glass rounded-xl p-1.5 shadow-2xl">
          {groups.map((g) => (
            <div key={g} className="mb-1 last:mb-0">
              <div className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">{g}</div>
              {models.filter((m) => m.group === g).map((m) => (
                <button key={m.id} data-testid={`model-option-${m.id}`}
                  onClick={() => { onChange(m.id); setOpen(false); }}
                  className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm transition-colors ${value === m.id ? "bg-indigo-500/15 text-white" : "text-slate-300 hover:bg-white/[0.05]"}`}>
                  <span className="flex flex-col items-start">
                    <span>{m.label}</span>
                    <span className="text-[10px] text-slate-500">{m.hint}</span>
                  </span>
                  {value === m.id && <Check className="h-3.5 w-3.5 text-indigo-400 shrink-0" />}
                </button>
              ))}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
